import React, { useState, useEffect } from 'react';
import RateHeader from './components/RateHeader';
import Calculator from './components/Calculator';
import BottomNav from './components/BottomNav';
import ContactCard from './components/ContactCard';
import AnnouncementBar from './components/AnnouncementBar';
import AdminPanel from './components/AdminPanel';
import GuideView from './components/GuideView';
import { ContactSettings } from './types';
import { db, auth, messaging, isFirebaseActive } from './services/firebaseConfig';
import { Bell, BellRing, Download } from 'lucide-react';

const App: React.FC = () => {
  const [view, setView] = useState<'home' | 'admin' | 'guide'>('home');
  const [announcementText, setAnnouncementText] = useState<string>('ယနေ့ ထိုင်း-မြန်မာ ငွေလဲလှယ်နှုန်းများကို အချိန်နှင့်တပြေးညီ တွက်ချက်နိုင်ပါပြီ။');
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');
  
  // PWA Install State
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallBtn, setShowInstallBtn] = useState<boolean>(false);
  const [isIOS, setIsIOS] = useState<boolean>(false);

  // Welcome Modal State
  const [showWelcomeModal, setShowWelcomeModal] = useState<boolean>(false);

  // Guide Images State
  const [guideImages, setGuideImages] = useState<string[]>([]);

  // FIREBASE CLOUD MESSAGING KEY
  const VAPID_KEY = "BGHrkCiT4WzE8Y1A1l7Q53WL6yMTHJHL09kBgN0sxgzNNWiR4WoVyW09WUhMY21-YueVUOpQ0_w9TG-1G9j4T8M"; 

  // Default Settings
  const defaultContactSettings: ContactSettings = {
    facebookUrl: 'https://www.facebook.com/yeWintNaung13579',
    messengerUrl: 'https://m.me/yeWintNaung13579',
    telegramUrl: 'https://t.me/DigitalsService',
    lineUrl: 'https://line.me/ti/p/~yewintnaung12101994',
    viberUrl: 'https://invite.viber.com/?g2=AQBiBCOmPlTEQlW0L4HIb7ceop0offPwoJNUxtVXlyznT7k6vOEcJ6ey1Zv3p1tj&lang=en',
    phoneNumber: '+66633727977',
    visibility: {
      facebook: true,
      messenger: true,
      viber: true,
      telegram: true,
      line: true,
      phone: true,
      more: true
    }
  };

  const [contactSettings, setContactSettings] = useState<ContactSettings>(defaultContactSettings);

  const saveTokenToDatabase = async (token: string) => {
    if (db) {
      if (auth && !auth.currentUser) {
          try { await auth.signInAnonymously(); } catch (e) {}
      }
      try {
        await db.collection('fcm_tokens').doc(token).set({
          createdAt: new Date().toISOString(),
          userAgent: navigator.userAgent,
          lastSeen: new Date().toISOString()
        }, { merge: true });
      } catch (e) {}
    }
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    }

    const userAgent = window.navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(userAgent));

    if ('Notification' in window && 'serviceWorker' in navigator) {
      setNotificationPermission(Notification.permission);
      if (Notification.permission === 'default') {
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone;
        if (!/iphone|ipad|ipod/.test(userAgent) || isStandalone) {
           setTimeout(() => setShowWelcomeModal(true), 2000);
        }
      }
    }
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallBtn(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  useEffect(() => {
    if (isFirebaseActive && db) {
      // Listen to Config
      const unsubConfig = db.collection('settings').doc('app_config').onSnapshot((docSnap: any) => {
        if (docSnap.exists) {
          const data = docSnap.data();
          if (data) {
             if (data.announcementText) setAnnouncementText(data.announcementText);
             if (data.contactSettings) setContactSettings(data.contactSettings);
          }
        }
      });

      // Listen to Guides (New split collection)
      const unsubGuides = db.collection('guides').orderBy('order').onSnapshot((snap: any) => {
        const images = snap.docs.map((doc: any) => doc.data().url);
        setGuideImages(images);
      });

      return () => {
        unsubConfig();
        unsubGuides();
      };
    }
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    setDeferredPrompt(null);
    setShowInstallBtn(false);
  };

  const requestNotificationPermission = async () => {
    if (!messaging || !isFirebaseActive) return;
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      setShowWelcomeModal(false); 
      if (permission === 'granted') {
        const swRegistration = await navigator.serviceWorker.ready;
        const token = await messaging.getToken({ vapidKey: VAPID_KEY, serviceWorkerRegistration: swRegistration });
        if (token) await saveTokenToDatabase(token);
      }
    } catch (error) {}
  };
  
  const handleLocalUpdate = (text: string, settings: ContactSettings, images: string[]) => {
    setAnnouncementText(text);
    setContactSettings(settings);
    setGuideImages(images);
    localStorage.setItem('announcementText', text);
    localStorage.setItem('contactSettings', JSON.stringify(settings));
    localStorage.setItem('guideImages', JSON.stringify(images));
  };

  const toggleTheme = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    document.documentElement.classList.toggle('dark', newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  return (
    <div className={`min-h-screen bg-slate-50 dark:bg-slate-900 relative font-sans max-w-md mx-auto shadow-2xl overflow-hidden border-x border-slate-200 dark:border-slate-800 transition-colors duration-300`}>
      <RateHeader />
      
      {view === 'home' && (
        <div onClick={() => isFirebaseActive && notificationPermission === 'default' && requestNotificationPermission()}>
           <AnnouncementBar text={announcementText} />
        </div>
      )}

      <main className="pb-24">
        {view === 'home' ? (
          <>
            <Calculator />
            
            <div className="mx-4 mb-4 flex flex-col gap-3">
              {showInstallBtn && (
                <button 
                  onClick={handleInstallClick}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 rounded-xl flex items-center justify-between shadow-md active:scale-95 transition-transform"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-lg"><Download size={20} /></div>
                    <div className="text-left">
                       <div className="font-bold text-sm font-myanmar">Application ဒေါင်းရန်</div>
                       <div className="text-xs opacity-90 font-myanmar">ဖုန်းတွင် အင်တာနက်မရှိလဲ အလွယ်တကူသုံးနိုင်သည်</div>
                    </div>
                  </div>
                  <span className="bg-white text-blue-600 text-xs font-bold px-3 py-1.5 rounded-full">Install</span>
                </button>
              )}
            </div>
            <ContactCard settings={contactSettings} />
          </>
        ) : view === 'admin' ? (
          <AdminPanel 
            currentText={announcementText}
            currentContactSettings={contactSettings}
            currentGuideImages={guideImages}
            onLocalUpdate={handleLocalUpdate}
            onBack={() => setView('home')}
          />
        ) : (
          <GuideView images={guideImages} onBack={() => setView('home')} />
        )}
      </main>

      {showWelcomeModal && view === 'home' && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
             <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <BellRing className="text-blue-600 w-8 h-8 animate-pulse" />
             </div>
             <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-2 font-myanmar">ဈေးနှုန်း အပ်ဒိတ်များ ရယူမလား?</h2>
             <button onClick={requestNotificationPermission} className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl mb-3 font-myanmar">လက်ခံမည် (Allow)</button>
             <button onClick={() => setShowWelcomeModal(false)} className="w-full text-gray-400 text-sm font-medium py-2">နောက်မှလုပ်မည်</button>
          </div>
        </div>
      )}

      <BottomNav 
        activeView={view} 
        onSettingsClick={() => setView(view === 'admin' ? 'home' : 'admin')} 
        onGuideClick={() => setView(view === 'guide' ? 'home' : 'guide')}
        isDarkMode={isDarkMode}
        toggleTheme={toggleTheme}
      />
    </div>
  );
};

export default App;